<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

import CheckSmIncon from '@lahaus-roomie/roomie/src/assets/icons/check-sm.svg'
import InfoIcon from '@lahaus-roomie/roomie/src/assets/icons/info.svg'
import RoomieRange from '@lahaus-roomie/roomie/src/components/RoomieRange/index.vue'
import RoomieTooltip from '@lahaus-roomie/roomie/src/components/RoomieTooltip/index.vue'

import type { Props } from './types'
import { calculateDownPayment, getMonthlyPayments } from '@/components/QuotationPlan/utils'

const MIN_DOWN_PAYMENT = 50_000
const DEFAULT_RANGE_STEP = 5000

const props = defineProps<Props>()

const { t, n } = useI18n()
const { $trackEvent } = useNuxtApp()

const initialDownPayment = computed(() => calculateDownPayment(props.quotationPlan.initial_fee_price, props.quotationPlan.separation_price))
const maxDownPayment = computed(() => initialDownPayment.value + props.quotationPlan.summary_final_plan_items.monthly_payment_price.value)

const initialPayment = ref<number>(initialDownPayment.value)

const monthlyPaymentsCount = computed(() => {
  const monthlyPaymentPrice = props.quotationPlan.summary_final_plan_items.monthly_payment_price.value

  return monthlyPaymentPrice ? getMonthlyPayments(props.quotationPlan.monthly_payments_and_additions).length : 0
})

const monthlyPayment = computed(() => {
  if (!monthlyPaymentsCount.value) return 0

  return Math.round((maxDownPayment.value - initialPayment.value) / monthlyPaymentsCount.value)
})
const lastPayment = computed(() => props.quotationPlan.summary_final_plan_items.credit_price.value)
const totalPayment = computed(() => props.quotationPlan.property_price)

const rangeStep = ref<number>(DEFAULT_RANGE_STEP)
const canShowMonthlyPayments = computed(() => monthlyPayment.value > 0)

const isDisclaimerVisible = ref(false)

const resetInitialPayment = () => {
  initialPayment.value = initialDownPayment.value
}

const trackSelectedValue = () => {
  $trackEvent({
    name: 'payment_plan_simulated',
    version: '1-0-0',
    data: {
      listings: [{ id: props.listingId, unit_id: props.unitCode }],
      initial_payment_value: initialPayment.value,
      monthly_payments_count: monthlyPaymentsCount.value,
      monthly_fee_value: monthlyPayment.value
    }
  })
}

watch(() => props.quotationPlan, () => {
  resetInitialPayment()
})

watch(initialPayment, (newInitialPayment) => {
  const minLimitToRound = maxDownPayment.value - DEFAULT_RANGE_STEP
  const maxLimitToRound = maxDownPayment.value

  const isBetweenRoundingBounds = (newInitialPayment > minLimitToRound) && (newInitialPayment <= maxLimitToRound)

  if (rangeStep.value === DEFAULT_RANGE_STEP && isBetweenRoundingBounds) {
    rangeStep.value = 1
    initialPayment.value = maxDownPayment.value
  } else if (newInitialPayment <= minLimitToRound && rangeStep.value !== DEFAULT_RANGE_STEP) {
    rangeStep.value = DEFAULT_RANGE_STEP
  }
})

</script>

<script lang="ts">
export default {
  name: 'QuotationPlanFeeSimulator'
}
</script>

<template>
  <div class="quotation-plan-fee-simulator">
    <div class="text-carbon-800 text-16 mb-4 flex">
      <span class="font-medium">
        {{ t('range.title') }}:
      </span>
    </div>

    <div class="font-medium text-carbon-800 text-26 flex items-center">
      {{ n(initialPayment, 'currency') }}

      <div class="relative">
        <InfoIcon
          id="quotation-plan-fee-simulator-disclaimer-icon"
          class="fill-carbon-300 w-24 h-24 ml-10"
          @mouseover="isDisclaimerVisible = true"
          @click="isDisclaimerVisible = !isDisclaimerVisible" />

        <RoomieTooltip
          id="quotation-plan-fee-simulator-disclaimer-tooltip"
          data-lh-id="quotation-plan-fee-simulator-disclaimer-tooltip"
          class="absolute"
          variant="message"
          :is-visible="isDisclaimerVisible"
          indicator-placement="bottom left"
          @tooltip-closed="isDisclaimerVisible = false">
          <template #description>
            {{ t('disclaimer') }}
          </template>
        </RoomieTooltip>
      </div>
    </div>

    <div class="mb-44 lg:mb-32">
      <RoomieRange
        id="quotation-plan-fee-simulator-range"
        v-model="initialPayment"
        data-lh-id="quotation-plan-fee-simulator-select-initial-fee"
        variant="brand"
        :step="rangeStep"
        class="mb-8"
        :min="MIN_DOWN_PAYMENT"
        :max="maxDownPayment"
        @mouseup="trackSelectedValue"
        @touchend="trackSelectedValue" />
    </div>

    <div class="flex gap-12">
      <div class="relative flex flex-col gap-24 w-fit">
        <div
          v-for="i in 3"
          :key="i"
          class="px-4 py-4 bg-success-50 w-fit rounded-full">
          <CheckSmIncon class="w-24 h-24 fill-success-600" />
        </div>

        <div class="absolute -ml-[1px] top-0 left-1/2 h-full border-l-2 border-dashed border-carbon-light-300 -z-10" />
      </div>

      <ul class="flex flex-col justify-between w-full">
        <li class="quotation-plan-fee-simulator__list-item">
          <span>
            <template v-if="canShowMonthlyPayments">
              {{ t('table.monthlyPayments.variable', { count: monthlyPaymentsCount }) }}
            </template>

            <template v-else>
              {{ t('table.monthlyPayments.fixed') }}
            </template>
          </span>

          <span
            class="quotation-plan-fee-simulator__list-item-value"
            :class="{'font-regular': !canShowMonthlyPayments}">
            <template v-if="canShowMonthlyPayments">
              {{ n(monthlyPayment, 'currency') }}
            </template>

            <template v-else>
              {{ t('notApply') }}
            </template>
          </span>
        </li>

        <li class="quotation-plan-fee-simulator__list-item">
          <span>
            {{ t('table.lastPayment') }}
          </span>

          <span class="quotation-plan-fee-simulator__list-item-value">
            {{ n(lastPayment, 'currency') }}
          </span>
        </li>

        <li class="quotation-plan-fee-simulator__list-item">
          <span>
            {{ t('table.totalPayment') }}
          </span>

          <span class="quotation-plan-fee-simulator__list-item-value">
            {{ n(totalPayment, 'currency') }}
          </span>
        </li>
      </ul>
    </div>

    <slot />
  </div>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss">
.quotation-plan-fee-simulator {
  &__list-item {
    @apply flex justify-between items-center text-carbon-600 text-14 lg:text-16;

    &-value {
      @apply font-medium text-right text-carbon-600 text-20 lg:text-22 ml-10;
    }
  }

  #quotation-plan-fee-simulator-disclaimer-tooltip {
    @apply z-20;

    top: -11.5rem;
    left: -9rem;

    .roomie-tooltip__indicator {
      left: 10.3rem;
    }
  }
}
</style>
